import React, { useEffect, useMemo, useState } from 'react';
import {
  MoreHorizontal,
  Eye,
  Copy,
  Trash2,
  Package,
  ChevronDown,
  Search,
  X,
  LayoutGrid,
  List,
  ArrowUpDown,
  Plus,
  Star,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { ProductLite } from '../useMarketplaceState';
import { ProductDraft } from './ProductDialog';
import { ProductDetailModal } from './ProductDetailModal';
import { isUnlinkedMicrosoftPlan } from '../microsoftLicenceLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/utils';

interface ProductsTabProps {
  products: ProductLite[];
  onCreateProduct: (domain?: string) => void;
  onDuplicate: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onDelete: (id: string) => void;
  onBulkDelete?: (ids: string[]) => void;
  onOpen: (id: string) => void;
  expandedProductId?: string | null;
  expandedProductDetails?: ProductDraft | null;
  isExpandedLoading?: boolean;
  onSaveExpanded?: () => void;
  onOpenImagePicker?: () => void;
  imagePickerOpen?: boolean;
  onUpdateExpanded?: (details: ProductDraft) => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  /** Initial search filter (e.g., from navigating from All Products catalogue) */
  initialSearchFilter?: string | null;
  /** Callback when the search filter is consumed/cleared */
  onClearInitialFilter?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  notice?: string | null;
  onDismissNotice?: () => void;
}

// ─── Catalogue dimensions ──────────────────────────────────────────────────
// Pulled out of the render so the chip strip + filter dropdowns share the
// same source of truth. Adding a new domain or source means editing one
// table only.

type CategoryKey =
  | 'all'
  | 'software'
  | 'computers'
  | 'networking'
  | 'mobile-hardware'
  | 'consulting'
  | 'telstra'
  | 'unified-collaboration';

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'software', label: 'Software' },
  { key: 'computers', label: 'Computers' },
  { key: 'networking', label: 'Networking' },
  { key: 'unified-collaboration', label: 'UC' },
  { key: 'consulting', label: 'Consulting' },
  { key: 'telstra', label: 'Telstra' },
];

type SourceKey = 'all' | 'manual' | 'dickerdata' | 'ingram' | 'telstra-distributor';
const SOURCES: { key: SourceKey; label: string }[] = [
  { key: 'all', label: 'All sources' },
  { key: 'manual', label: 'Manual' },
  { key: 'dickerdata', label: 'Dicker Data' },
  { key: 'ingram', label: 'Ingram Micro' },
  { key: 'telstra-distributor', label: 'Telstra' },
];

type StatusKey = 'all' | 'active' | 'hidden';
type StockKey = 'all' | 'in-stock' | 'low-stock' | 'out-of-stock';

type SortKey =
  | 'name-asc'
  | 'name-desc'
  | 'price-asc'
  | 'price-desc'
  | 'stock-desc'
  | 'category';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'name-asc', label: 'Name (A → Z)' },
  { key: 'name-desc', label: 'Name (Z → A)' },
  { key: 'price-asc', label: 'Price (low → high)' },
  { key: 'price-desc', label: 'Price (high → low)' },
  { key: 'stock-desc', label: 'Stock (high → low)' },
  { key: 'category', label: 'Category' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
const VIEW_STORAGE_KEY = 'aitryx-partner.products.viewMode';
const PAGE_SIZE_STORAGE_KEY = 'aitryx-partner.products.pageSize';

// ─── Helpers ───────────────────────────────────────────────────────────────

const getDomain = (p: ProductLite): string => {
  const stored = (p as any).domain as string | undefined;
  if (stored) return stored;
  if (p.kind === 'software') return 'software';
  if (p.kind === 'consulting') return 'consulting';
  return 'computers';
};

const getDomainLabel = (p: ProductLite): string => {
  const labels: Record<string, string> = {
    software: 'Software',
    computers: 'Computers',
    networking: 'Networking',
    'mobile-hardware': 'Telstra Handset',
    consulting: 'Consulting',
    telstra: 'Telstra Plan',
    'unified-collaboration': 'Unified Collab',
  };
  return labels[getDomain(p)] || getDomain(p);
};

const getDomainBadgeVariant = (
  p: ProductLite,
): 'info' | 'purple' | 'warning' | 'secondary' | 'success' | 'default' => {
  const domain = getDomain(p);
  if (domain === 'telstra' || domain === 'mobile-hardware') return 'info';
  if (domain === 'software') return 'purple';
  if (domain === 'consulting') return 'warning';
  if (domain === 'networking') return 'success';
  return 'secondary';
};

const matchesCategoryFilter = (domain: string, filter: CategoryKey): boolean => {
  if (filter === 'all') return true;
  if (filter === 'telstra') return domain === 'telstra' || domain === 'mobile-hardware';
  return domain === filter;
};

const sourceLabel = (s?: string): string => {
  switch (s) {
    case 'dickerdata': return 'Dicker Data';
    case 'ingram': return 'Ingram Micro';
    case 'telstra-distributor': return 'Telstra';
    case 'manual': return 'Manual';
    default: return 'Manual';
  }
};
const sourceBadgeStyles = (s?: string): string => {
  switch (s) {
    case 'dickerdata': return 'bg-cyan-50 text-cyan-700 ring-cyan-200';
    case 'ingram': return 'bg-blue-50 text-blue-700 ring-blue-200';
    case 'telstra-distributor': return 'bg-violet-50 text-violet-700 ring-violet-200';
    default: return 'bg-slate-100 text-slate-600 ring-slate-200';
  }
};
const sourceShort = (s?: string): string => {
  switch (s) {
    case 'dickerdata': return 'DD';
    case 'ingram': return 'IM';
    case 'telstra-distributor': return 'TE';
    default: return 'M';
  }
};

/** Strip a price string like "$1,234.50" or "$45/mo" → 1234.5 / 45. */
const parsePriceForSort = (p: ProductLite): number => {
  if (typeof p.sellPrice === 'number') return p.sellPrice;
  if (typeof p.buyPrice === 'number') return p.buyPrice;
  const raw = p.priceDisplay || '';
  const m = raw.match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return Number.POSITIVE_INFINITY;
  return Number.parseFloat(m[0].replace(/,/g, ''));
};

const stockKind = (p: ProductLite): 'in-stock' | 'low-stock' | 'out-of-stock' | 'na' => {
  const distributor =
    p.source === 'dickerdata' || p.source === 'ingram' || p.source === 'telstra-distributor';
  if (!distributor || (p as any).domain === 'software') return 'na';
  const soh = p.soh ?? 0;
  if (soh > 10) return 'in-stock';
  if (soh > 0) return 'low-stock';
  return 'out-of-stock';
};

// ─── Component ─────────────────────────────────────────────────────────────

export function ProductsTab(props: ProductsTabProps) {
  const {
    products,
    onCreateProduct,
    onDuplicate,
    onToggleStatus,
    onDelete,
    onBulkDelete,
    onOpen,
    expandedProductId,
    expandedProductDetails,
    isExpandedLoading,
    onSaveExpanded,
    onOpenImagePicker,
    imagePickerOpen,
    onUpdateExpanded,
    getCachedImageUrl,
    initialSearchFilter,
    onClearInitialFilter,
    onRefresh,
    refreshing,
    notice,
    onDismissNotice,
  } = props;

  // ── Filter / sort / view state ────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryKey>('all');
  const [statusFilter, setStatusFilter] = useState<StatusKey>('all');
  const [stockFilter, setStockFilter] = useState<StockKey>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceKey>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name-asc');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      const n = saved ? Number(saved) : 25;
      return PAGE_SIZE_OPTIONS.includes(n) ? n : 25;
    } catch {
      return 25;
    }
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => {
    try {
      const saved = localStorage.getItem(VIEW_STORAGE_KEY);
      return saved === 'grid' ? 'grid' : 'table';
    } catch {
      return 'table';
    }
  });

  // Persist UI choices that should outlive a tab switch.
  useEffect(() => {
    try { localStorage.setItem(VIEW_STORAGE_KEY, viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  useEffect(() => {
    try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize)); } catch { /* ignore */ }
  }, [pageSize]);

  // Apply incoming search filter (e.g. from "All Products" catalogue link).
  useEffect(() => {
    if (initialSearchFilter) {
      setQuery(initialSearchFilter);
      onClearInitialFilter?.();
    }
  }, [initialSearchFilter, onClearInitialFilter]);

  // ── Filter pipeline ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const domain = getDomain(p);

      const matchesCategory = matchesCategoryFilter(domain, categoryFilter);

      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;

      const matchesSource =
        sourceFilter === 'all' || (p.source ?? 'manual') === sourceFilter;

      const sk = stockKind(p);
      const matchesStock =
        stockFilter === 'all' || (sk !== 'na' && sk === stockFilter);

      const matchesQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.vendorOrBrand || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q);

      return matchesCategory && matchesStatus && matchesSource && matchesStock && matchesQuery;
    });
  }, [products, query, categoryFilter, statusFilter, stockFilter, sourceFilter]);

  // Sort happens after filtering so it's predictable across filter changes.
  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sortKey) {
      case 'name-asc':
        return arr.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return arr.sort((a, b) => b.name.localeCompare(a.name));
      case 'price-asc':
        return arr.sort((a, b) => parsePriceForSort(a) - parsePriceForSort(b));
      case 'price-desc':
        return arr.sort((a, b) => parsePriceForSort(b) - parsePriceForSort(a));
      case 'stock-desc':
        return arr.sort((a, b) => (b.soh ?? -1) - (a.soh ?? -1));
      case 'category':
        return arr.sort((a, b) =>
          getDomainLabel(a).localeCompare(getDomainLabel(b)) || a.name.localeCompare(b.name),
        );
      default:
        return arr;
    }
  }, [filtered, sortKey]);

  // Reset page when anything that changes the result set fires.
  useEffect(() => {
    setPageIndex(0);
  }, [query, categoryFilter, statusFilter, stockFilter, sourceFilter, sortKey, pageSize, viewMode]);

  // ── Pagination ────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const paginated = sorted.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize);
  const showingFrom = sorted.length > 0 ? safePageIndex * pageSize + 1 : 0;
  const showingTo = Math.min((safePageIndex + 1) * pageSize, sorted.length);

  // ── Selection ─────────────────────────────────────────────────────────
  const allOnPageSelected = paginated.length > 0 && paginated.every((p) => selectedIds.has(p.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        paginated.forEach((p) => next.delete(p.id));
      } else {
        paginated.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const toggleSelect = (id: string, e?: React.MouseEvent | React.ChangeEvent) => {
    e?.stopPropagation?.();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (!onBulkDelete) return;
    const count = selectedIds.size;
    if (window.confirm(`Delete ${count} selected product${count > 1 ? 's' : ''}? This cannot be undone.`)) {
      onBulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  // ── Active-filter chips ───────────────────────────────────────────────
  // Aggregated view of every non-default filter so the partner can see at a
  // glance what's narrowing the list. Each chip removes its own filter.
  type ActiveChip = { key: string; label: string; onClear: () => void };
  const activeChips: ActiveChip[] = [];
  if (categoryFilter !== 'all') {
    const c = CATEGORIES.find((x) => x.key === categoryFilter);
    activeChips.push({
      key: `cat-${categoryFilter}`,
      label: `Category: ${c?.label ?? categoryFilter}`,
      onClear: () => setCategoryFilter('all'),
    });
  }
  if (sourceFilter !== 'all') {
    const s = SOURCES.find((x) => x.key === sourceFilter);
    activeChips.push({
      key: `src-${sourceFilter}`,
      label: `Source: ${s?.label ?? sourceFilter}`,
      onClear: () => setSourceFilter('all'),
    });
  }
  if (statusFilter !== 'all') {
    activeChips.push({
      key: `st-${statusFilter}`,
      label: `Status: ${statusFilter === 'active' ? 'Active' : 'Hidden'}`,
      onClear: () => setStatusFilter('all'),
    });
  }
  if (stockFilter !== 'all') {
    const lbl = stockFilter === 'in-stock' ? 'In stock' : stockFilter === 'low-stock' ? 'Low stock' : 'Out of stock';
    activeChips.push({
      key: `stk-${stockFilter}`,
      label: `Stock: ${lbl}`,
      onClear: () => setStockFilter('all'),
    });
  }
  if (query.trim()) {
    activeChips.push({
      key: 'q',
      label: `Search: "${query.trim()}"`,
      onClear: () => setQuery(''),
    });
  }
  const clearAllFilters = () => {
    setQuery('');
    setCategoryFilter('all');
    setStatusFilter('all');
    setStockFilter('all');
    setSourceFilter('all');
  };

  // Per-category counts for the chip strip — surfaces what's actually in
  // the catalogue without making the partner click through and find empty
  // tabs.
  const categoryCounts = useMemo(() => {
    const counts: Record<CategoryKey, number> = {
      all: products.length,
      software: 0,
      computers: 0,
      networking: 0,
      'mobile-hardware': 0,
      consulting: 0,
      telstra: 0,
      'unified-collaboration': 0,
    };
    for (const p of products) {
      const d = getDomain(p) as CategoryKey;
      if (d === 'mobile-hardware') {
        counts.telstra += 1;
      } else if (d in counts) {
        counts[d] += 1;
      }
    }
    return counts;
  }, [products]);

  const currentSortLabel = SORT_OPTIONS.find((s) => s.key === sortKey)?.label ?? 'Sort';

  // ── Render ────────────────────────────────────────────────────────────
  // Two-column layout matching the aitryx-customer secondary-pane pattern:
  //   - Left: filter pane (Categories / Source / Status / Stock) — slim flat
  //     rows, orange-50 active highlight, hairline section dividers, own
  //     scroll container so long category lists never crowd the result area.
  //   - Right: header + sticky toolbar (search + sort + view toggle + active
  //     chips) + result region + pagination footer; this column owns the
  //     vertical page scroll the user expects.
  return (
    <div className="h-full flex bg-slate-50 overflow-hidden">
      {/* ── Filter pane (secondary nav) ────────────────────────────────── */}
      <FilterPane
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        stockFilter={stockFilter}
        setStockFilter={setStockFilter}
        categoryCounts={categoryCounts}
        activeChipCount={activeChips.length}
        onClearAll={clearAllFilters}
      />

      {/* ── Right column ───────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 h-full overflow-y-auto flex flex-col">
        {/* Header — title + result count + Add CTA */}
        <div className="px-6 pt-5 pb-3 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 m-0 leading-tight">Products</h1>
            <p className="text-sm text-slate-500 mt-0.5 m-0">
              {sorted.length === products.length
                ? `${products.length.toLocaleString()} ${products.length === 1 ? 'product' : 'products'} in your catalogue`
                : `${sorted.length.toLocaleString()} of ${products.length.toLocaleString()} products`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => onCreateProduct()} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add product
            </Button>
          </div>
        </div>

        {/* Sticky toolbar — narrower now that filters live in the pane.
            Just search, sort, view toggle, bulk actions, and the active
            chip strip. */}
        <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur supports-[backdrop-filter]:bg-slate-50/80 border-b border-slate-200 px-6 pt-1 pb-3 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, SKU, or vendor…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-10 pl-9 pr-9 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-shadow shadow-sm"
              aria-label="Search products"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label="Clear search"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Sort + bulk + view toggle row */}
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5">
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  <span className="font-semibold">{currentSortLabel}</span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[200px]">
                <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SORT_OPTIONS.map((o) => (
                  <DropdownMenuItem
                    key={o.key}
                    onClick={() => setSortKey(o.key)}
                    className={cn(sortKey === o.key && 'bg-orange-50 font-semibold text-orange-800')}
                  >
                    {o.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {someSelected && onBulkDelete && (
              <div className="flex items-center gap-2 ml-1">
                <span className="text-xs font-medium text-slate-600">
                  {selectedIds.size} selected
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      Bulk actions <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={handleBulkDelete} className="text-rose-600 focus:text-rose-600">
                      <Trash2 className="mr-2 h-4 w-4" /> Delete selected
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setSelectedIds(new Set())}>
                      Clear selection
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <div className="ml-auto inline-flex items-center gap-2">
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1.5 h-8 rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                  title="Refresh the catalogue"
                  aria-label="Refresh the catalogue"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
                  Refresh
                </button>
              )}
            <div className="inline-flex items-center rounded-md border border-slate-300 bg-white p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={cn(
                  'inline-flex items-center justify-center h-7 w-8 rounded transition-colors',
                  viewMode === 'table'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50',
                )}
                title="Table view"
                aria-label="Switch to table view"
                aria-pressed={viewMode === 'table'}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={cn(
                  'inline-flex items-center justify-center h-7 w-8 rounded transition-colors',
                  viewMode === 'grid'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50',
                )}
                title="Grid view"
                aria-label="Switch to grid view"
                aria-pressed={viewMode === 'grid'}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
            </div>
          </div>

          {notice && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <span>{notice}</span>
              {onDismissNotice && (
                <button type="button" onClick={onDismissNotice} className="text-emerald-700 hover:text-emerald-900" aria-label="Dismiss">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Active filter chip strip — single readout of every applied
              filter. Mirrors the pane state, but each chip is also
              individually clearable. */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wide font-mono font-semibold text-slate-500 mr-1">
                Filters
              </span>
              {activeChips.map((c) => (
                <span
                  key={c.key}
                  className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-300 pl-2.5 pr-1 h-6 text-[11px] font-medium text-slate-700"
                >
                  {c.label}
                  <button
                    type="button"
                    onClick={c.onClear}
                    className="inline-flex items-center justify-center h-4 w-4 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    aria-label={`Remove ${c.label}`}
                    title="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearAllFilters}
                className="ml-1 text-[11px] font-medium text-orange-700 hover:text-orange-800 underline-offset-2 hover:underline bg-transparent border-0"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Result region */}
        <div className="flex-1 px-6 py-4">
          {sorted.length === 0 ? (
            <EmptyState
              hasProducts={products.length > 0}
              hasFilters={activeChips.length > 0}
              onAdd={() => onCreateProduct()}
              onClear={clearAllFilters}
            />
          ) : viewMode === 'grid' ? (
            <GridView
              products={paginated}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onOpen={onOpen}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onToggleStatus={onToggleStatus}
              getCachedImageUrl={getCachedImageUrl}
              showSelection={!!onBulkDelete}
            />
          ) : (
            <TableView
              products={paginated}
              selectedIds={selectedIds}
              allOnPageSelected={allOnPageSelected}
              onToggleSelectAll={toggleSelectAll}
              onToggleSelect={toggleSelect}
              onOpen={onOpen}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onToggleStatus={onToggleStatus}
              getCachedImageUrl={getCachedImageUrl}
              expandedProductId={expandedProductId}
              showSelection={!!onBulkDelete}
            />
          )}
        </div>

        {/* Pagination footer */}
        {sorted.length > 0 && (
        <div className="border-t border-slate-200 bg-white px-6 py-2.5 flex items-center justify-between gap-4 text-sm">
          <div className="text-slate-600">
            Showing <span className="font-semibold text-slate-900">{showingFrom.toLocaleString()}</span>
            {' – '}
            <span className="font-semibold text-slate-900">{showingTo.toLocaleString()}</span>
            {' of '}
            <span className="font-semibold text-slate-900">{sorted.length.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-slate-600">
              <span className="hidden sm:inline">Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-8 px-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
                aria-label="Show entries per page"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-0.5 border-l border-slate-200 pl-3">
              <Button
                variant="ghost" size="icon-sm" className="h-8 w-8"
                onClick={() => setPageIndex(0)}
                disabled={safePageIndex === 0}
                title="First page" aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="icon-sm" className="h-8 w-8"
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                disabled={safePageIndex === 0}
                title="Previous page" aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="px-3 py-1 text-sm text-slate-700 min-w-[5.5rem] text-center">
                Page <span className="font-semibold">{safePageIndex + 1}</span>
                <span className="text-slate-400"> / {totalPages}</span>
              </div>
              <Button
                variant="ghost" size="icon-sm" className="h-8 w-8"
                onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePageIndex >= totalPages - 1}
                title="Next page" aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="icon-sm" className="h-8 w-8"
                onClick={() => setPageIndex(totalPages - 1)}
                disabled={safePageIndex >= totalPages - 1}
                title="Last page" aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        )}
      </div>
      {/* /right column */}

      <ProductDetailModal
        open={!!expandedProductId}
        product={expandedProductDetails ?? null}
        isLoading={isExpandedLoading}
        onClose={() => { if (expandedProductId) onOpen(expandedProductId); }}
        onSave={() => { onSaveExpanded?.(); }}
        onUpdate={(next) => onUpdateExpanded?.(next)}
        onOpenImagePicker={onOpenImagePicker}
        imagePickerOpen={imagePickerOpen}
        onDelete={expandedProductId ? () => onDelete(expandedProductId) : undefined}
        getCachedImageUrl={getCachedImageUrl}
        otherProducts={products}
      />
    </div>
  );
}

// ─── Filter pane (left secondary nav) ──────────────────────────────────────
// Slim flat-row sidebar that owns the four filter dimensions: Category,
// Source, Status, Stock. Mirrors the aitryx-customer admin secondary-pane
// pattern — uppercase mono section labels, hairline dividers, h-9 rows
// with orange-50 active fill + orange-700 leading bullet. Each section is
// a radio group; the badge on Categories is the live count from the
// underlying products array (so empty categories visibly disable).

interface FilterPaneProps {
  categoryFilter: CategoryKey;
  setCategoryFilter: (k: CategoryKey) => void;
  sourceFilter: SourceKey;
  setSourceFilter: (k: SourceKey) => void;
  statusFilter: StatusKey;
  setStatusFilter: (k: StatusKey) => void;
  stockFilter: StockKey;
  setStockFilter: (k: StockKey) => void;
  categoryCounts: Record<CategoryKey, number>;
  activeChipCount: number;
  onClearAll: () => void;
}

const STATUSES: { key: StatusKey; label: string }[] = [
  { key: 'all', label: 'All statuses' },
  { key: 'active', label: 'Active' },
  { key: 'hidden', label: 'Hidden' },
];

const STOCKS: { key: StockKey; label: string }[] = [
  { key: 'all', label: 'All stock levels' },
  { key: 'in-stock', label: 'In stock (10+)' },
  { key: 'low-stock', label: 'Low (1–10)' },
  { key: 'out-of-stock', label: 'Out of stock' },
];

interface FilterRowProps {
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: React.ReactNode;
  disabled?: boolean;
}

const FilterRow: React.FC<FilterRowProps> = ({ label, active, onClick, badge, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    aria-pressed={active}
    className={cn(
      'inline-flex w-full items-center gap-2.5 h-9 pl-2.5 pr-2 rounded-md text-sm transition-colors',
      'border border-transparent',
      active
        ? 'bg-orange-50 text-slate-900 font-semibold'
        : disabled
        ? 'text-slate-300 cursor-not-allowed'
        : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
    )}
  >
    <span
      aria-hidden
      className={cn(
        'h-1.5 w-1.5 rounded-full shrink-0',
        active ? 'bg-orange-600' : disabled ? 'bg-slate-200' : 'bg-slate-300',
      )}
    />
    <span className="flex-1 text-left truncate">{label}</span>
    {badge}
  </button>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border-t border-slate-200/80 mt-3 pt-2.5 mb-1 px-2.5">
    <span className="font-mono text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400">
      {children}
    </span>
  </div>
);

const FilterPane: React.FC<FilterPaneProps> = ({
  categoryFilter, setCategoryFilter,
  sourceFilter, setSourceFilter,
  statusFilter, setStatusFilter,
  stockFilter, setStockFilter,
  categoryCounts,
  activeChipCount,
  onClearAll,
}) => (
  <aside className="w-60 shrink-0 h-full overflow-y-auto bg-white border-r border-slate-200">
    <div className="px-2.5 pt-3 pb-2 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-slate-900 px-1">Filters</h2>
      {activeChipCount > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex items-center text-[11px] font-medium text-orange-700 hover:text-orange-800 underline-offset-2 hover:underline bg-transparent border-0 px-1.5 h-6 rounded"
        >
          Clear all
        </button>
      )}
    </div>

    <SectionLabel>Categories</SectionLabel>
    <div className="px-1.5 space-y-0.5">
      {CATEGORIES.map((c) => {
        const count = categoryCounts[c.key];
        const isEmpty = count === 0 && c.key !== 'all';
        return (
          <FilterRow
            key={c.key}
            label={c.label}
            active={categoryFilter === c.key}
            onClick={() => setCategoryFilter(c.key)}
            disabled={isEmpty}
            badge={
              <span
                className={cn(
                  'inline-flex items-center justify-center h-[18px] min-w-[24px] px-1.5 rounded-full text-[10px] font-mono font-semibold',
                  categoryFilter === c.key
                    ? 'bg-orange-100 text-orange-700'
                    : isEmpty
                    ? 'bg-slate-50 text-slate-300'
                    : 'bg-slate-100 text-slate-500',
                )}
              >
                {count.toLocaleString()}
              </span>
            }
          />
        );
      })}
    </div>

    <SectionLabel>Source</SectionLabel>
    <div className="px-1.5 space-y-0.5">
      {SOURCES.map((s) => (
        <FilterRow
          key={s.key}
          label={s.label}
          active={sourceFilter === s.key}
          onClick={() => setSourceFilter(s.key)}
        />
      ))}
    </div>

    <SectionLabel>Status</SectionLabel>
    <div className="px-1.5 space-y-0.5">
      {STATUSES.map((s) => (
        <FilterRow
          key={s.key}
          label={s.label}
          active={statusFilter === s.key}
          onClick={() => setStatusFilter(s.key)}
        />
      ))}
    </div>

    <SectionLabel>Stock</SectionLabel>
    <div className="px-1.5 space-y-0.5 pb-4">
      {STOCKS.map((s) => (
        <FilterRow
          key={s.key}
          label={s.label}
          active={stockFilter === s.key}
          onClick={() => setStockFilter(s.key)}
        />
      ))}
    </div>
  </aside>
);

// ─── Empty state ───────────────────────────────────────────────────────────
// Splits into two distinct copies so the partner knows whether they need to
// add a product or just relax their filters.

interface EmptyStateProps {
  hasProducts: boolean;
  hasFilters: boolean;
  onAdd: () => void;
  onClear: () => void;
}
const EmptyState: React.FC<EmptyStateProps> = ({ hasProducts, hasFilters, onAdd, onClear }) => (
  <div className="border border-dashed border-slate-300 rounded-xl bg-white">
    <div className="flex flex-col items-center justify-center py-20 px-5 text-center">
      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <Package className="w-8 h-8 text-slate-400" />
      </div>
      {hasProducts && hasFilters ? (
        <>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">No products match these filters</h3>
          <p className="text-slate-500 mb-4 text-sm max-w-sm">
            Try clearing some filters or broadening your search to see more of your catalogue.
          </p>
          <Button variant="outline" onClick={onClear}>
            Clear all filters
          </Button>
        </>
      ) : (
        <>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Your catalogue is empty</h3>
          <p className="text-slate-500 mb-4 text-sm max-w-sm">
            Add your first product manually, or sync from a connected distributor in the Distributors tab.
          </p>
          <Button onClick={onAdd} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Add product
          </Button>
        </>
      )}
    </div>
  </div>
);

// ─── Status pill (shared by table + grid) ──────────────────────────────────
// Lifted out so the same dropdown affordance powers both views.

interface StatusPillProps {
  product: ProductLite;
  onToggle: (id: string) => void;
}
const StatusPill: React.FC<StatusPillProps> = ({ product, onToggle }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors cursor-pointer',
          product.status === 'active'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
            : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200',
        )}
        title="Change status"
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', product.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400')} />
        {product.status === 'active' ? 'Active' : 'Hidden'}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <DropdownMenuItem onClick={() => { if (product.status !== 'active') onToggle(product.id); }}>
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-2" /> Active
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => { if (product.status === 'active') onToggle(product.id); }}>
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 mr-2" /> Hidden
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// ─── Action menu ───────────────────────────────────────────────────────────

interface ActionMenuProps {
  productId: string;
  onOpen: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}
const ActionMenu: React.FC<ActionMenuProps> = ({ productId, onOpen, onDuplicate, onDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Actions" aria-label="Product actions"
      >
        <MoreHorizontal className="h-4 w-4 text-slate-500" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={() => onOpen(productId)}>
        <Eye className="mr-2 h-4 w-4" /> View / Edit
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onDuplicate(productId)}>
        <Copy className="mr-2 h-4 w-4" /> Duplicate
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => onDelete(productId)} className="text-rose-600 focus:text-rose-600">
        <Trash2 className="mr-2 h-4 w-4" /> Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// ─── Source pill (shared) ──────────────────────────────────────────────────

const SourcePill: React.FC<{ source?: string }> = ({ source }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
      sourceBadgeStyles(source),
    )}
    title={`Sourced from ${sourceLabel(source)}`}
  >
    <span className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-sm bg-white/60 text-[8px] font-bold tabular-nums">
      {sourceShort(source)}
    </span>
    {sourceLabel(source)}
  </span>
);

// ─── Licence link pill (shared) ────────────────────────────────────────────

const LicenceNotLinkedBadge: React.FC = () => (
  <span
    title="Not linked to a Microsoft licence, so customers can't buy extra seats of it from the New User form. Open the product to link it."
    className="flex-shrink-0"
  >
    <Badge variant="warning" size="sm">Licence not linked</Badge>
  </span>
);

// ─── Stock pill (shared) ───────────────────────────────────────────────────

const StockPill: React.FC<{ product: ProductLite }> = ({ product }) => {
  const kind = stockKind(product);
  if (kind === 'na') return <span className="text-slate-400 text-xs">—</span>;
  const variant = kind === 'in-stock' ? 'success' : kind === 'low-stock' ? 'warning' : 'secondary';
  const label =
    kind === 'in-stock'
      ? `In stock (${product.soh})`
      : kind === 'low-stock'
      ? `Low (${product.soh})`
      : 'Out of stock';
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// ─── Table view ────────────────────────────────────────────────────────────

interface TableViewProps {
  products: ProductLite[];
  selectedIds: Set<string>;
  allOnPageSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelect: (id: string, e: React.MouseEvent | React.ChangeEvent) => void;
  onOpen: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  expandedProductId?: string | null;
  showSelection: boolean;
}
const TableView: React.FC<TableViewProps> = ({
  products,
  selectedIds,
  allOnPageSelected,
  onToggleSelectAll,
  onToggleSelect,
  onOpen,
  onDuplicate,
  onDelete,
  onToggleStatus,
  getCachedImageUrl,
  expandedProductId,
  showSelection,
}) => {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80">
            <tr className="border-b border-slate-200">
              {showSelection && (
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={onToggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                    aria-label="Select all on page"
                  />
                </th>
              )}
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide w-14">Image</th>
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide">Product</th>
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide">Category</th>
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide w-32">Source</th>
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide">Price</th>
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide">Stock</th>
              <th className="px-3 py-2.5 text-left font-semibold text-slate-600 uppercase text-[11px] tracking-wide">Status</th>
              <th className="px-3 py-2.5 text-right font-semibold text-slate-600 uppercase text-[11px] tracking-wide w-12">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p, index) => (
              <tr
                key={p.id}
                onClick={() => onOpen(p.id)}
                className={cn(
                  'border-b border-slate-100 transition-colors cursor-pointer hover:bg-slate-50',
                  selectedIds.has(p.id) && 'bg-orange-50/40',
                  expandedProductId === p.id && 'bg-slate-50',
                  index === products.length - 1 && 'border-b-0',
                )}
              >
                {showSelection && (
                  <td className="px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => {}}
                      onClick={(e) => onToggleSelect(p.id, e)}
                      className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                      aria-label={`Select ${p.name}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
                    {p.image ? (
                      <img
                        src={getCachedImageUrl ? getCachedImageUrl(p.image) : p.image}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-slate-900 truncate">{p.name}</span>
                    {p.featured && (
                      <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 flex-shrink-0" aria-label="Featured" />
                    )}
                    {isUnlinkedMicrosoftPlan(p) && <LicenceNotLinkedBadge />}
                  </div>
                  {(p.sku || p.vendorOrBrand) && (
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {p.vendorOrBrand && <span>{p.vendorOrBrand}</span>}
                      {p.vendorOrBrand && p.sku && <span className="mx-1.5 text-slate-300">·</span>}
                      {p.sku && <span className="font-mono">{p.sku}</span>}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Badge variant={getDomainBadgeVariant(p)} size="sm">
                    {getDomainLabel(p)}
                  </Badge>
                </td>
                <td className="px-3 py-2.5">
                  <SourcePill source={p.source} />
                </td>
                <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                  {p.priceDisplay || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2.5">
                  <StockPill product={p} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusPill product={p} onToggle={onToggleStatus} />
                </td>
                <td className="px-3 py-2.5 text-right">
                  <ActionMenu
                    productId={p.id}
                    onOpen={onOpen}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Grid view ─────────────────────────────────────────────────────────────
// Card-per-product layout for visual scanning. Hover surfaces the kebab and
// (when selection is on) the checkbox; otherwise the cards stay clean.

interface GridViewProps {
  products: ProductLite[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string, e: React.MouseEvent | React.ChangeEvent) => void;
  onOpen: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  showSelection: boolean;
}
const GridView: React.FC<GridViewProps> = ({
  products,
  selectedIds,
  onToggleSelect,
  onOpen,
  onDuplicate,
  onDelete,
  onToggleStatus,
  getCachedImageUrl,
  showSelection,
}) => (
  <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
    {products.map((p) => {
      const isSelected = selectedIds.has(p.id);
      return (
        <div
          key={p.id}
          onClick={() => onOpen(p.id)}
          className={cn(
            'group relative bg-white border rounded-xl overflow-hidden cursor-pointer transition-all',
            'hover:shadow-md hover:border-slate-300',
            isSelected ? 'border-orange-400 ring-2 ring-orange-200' : 'border-slate-200',
          )}
        >
          {/* Image — square container so cards align even when source images
              vary in aspect ratio. Slate-50 lets the white-bg product photos
              breathe. */}
          <div className="relative aspect-square bg-slate-50 border-b border-slate-100 overflow-hidden">
            {p.image ? (
              <img
                src={getCachedImageUrl ? getCachedImageUrl(p.image) : p.image}
                alt={p.name}
                className="w-full h-full object-contain p-3"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                <Package className="w-10 h-10" />
              </div>
            )}

            {/* Featured ribbon — visible at the top corner without competing
                with selection / kebab affordances on the opposite corner. */}
            {p.featured && (
              <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-amber-500/90 text-white px-2 py-0.5 text-[10px] font-semibold shadow-sm">
                <Star className="h-2.5 w-2.5 fill-white" /> Featured
              </div>
            )}

            {/* Selection checkbox — always visible when checked, otherwise
                fades in on hover to avoid noise on the grid. */}
            {showSelection && (
              <label
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  'absolute top-2 right-10 inline-flex items-center justify-center h-6 w-6 rounded-md bg-white/90 border border-slate-200 shadow-sm transition-opacity',
                  isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onToggleSelect(p.id, e)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  aria-label={`Select ${p.name}`}
                />
              </label>
            )}

            {/* Kebab — uses the shared menu so action parity with the table
                is automatic. Stop propagation so the menu doesn't open the
                detail modal beneath. */}
            <div
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white/90 rounded-md shadow-sm border border-slate-200">
                <ActionMenu
                  productId={p.id}
                  onOpen={onOpen}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                />
              </div>
            </div>

            {/* Stock corner badge — only on distributor products, anchored to
                bottom-right so it doesn't overlap the title. */}
            {stockKind(p) !== 'na' && (
              <div className="absolute bottom-2 left-2">
                <StockPill product={p} />
              </div>
            )}
          </div>

          {/* Body — name, vendor·SKU, price, category + status row. */}
          <div className="p-3 space-y-1.5">
            <div className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 min-h-[2.5rem]">
              {p.name}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {p.vendorOrBrand || <span className="text-slate-400">No vendor</span>}
              {p.sku && (
                <>
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span className="font-mono">{p.sku}</span>
                </>
              )}
            </div>
            <div className="text-sm font-semibold text-slate-900 pt-0.5">
              {p.priceDisplay || <span className="text-slate-400 font-normal">No price</span>}
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <Badge variant={getDomainBadgeVariant(p)} size="sm">
                {getDomainLabel(p)}
              </Badge>
              <StatusPill product={p} onToggle={onToggleStatus} />
            </div>
            <div className="pt-0.5 flex flex-wrap items-center gap-1">
              <SourcePill source={p.source} />
              {isUnlinkedMicrosoftPlan(p) && <LicenceNotLinkedBadge />}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default ProductsTab;
